import { memo, useCallback, useMemo } from "react";
import { amp } from "src/api/amplitude";
import PhotoVariant from "src/api/graphql/__generated__/enums/PhotoVariant";
import { ShareWrapper } from "src/components/ShareMenu";
import { SharePayload, SharePayloadProp, ValidShareOption } from "src/components/ShareMenu/types";
import { useGatheringStore } from "src/contexts/GatheringContext";
import parseV0TemplateUrl from "src/helpers/parseV0TemplateUrl";
import { useShallow } from "zustand/shallow";
import FloatingSelectActionsContent from "./components/FloatingSelectActionsContent";

const FloatingSelectActions = () => {
	const selectedPhotos = useGatheringStore(
		useShallow((state) => Object.values(state.selectedPhotos)),
	);
	const resetSelectMode = useGatheringStore((state) => state.resetSelectMode);
	const shareData = useMemo<SharePayloadProp["data"]>(() => {
		return selectedPhotos
			.map((photo) => {
				const { variant } = photo;
				const successAmplitude = {
					eventName: amp.photo.shareAction,
					eventProperties: {
						photoId: photo.photoId,
						ownerId: photo.ownerId,
						isTopPhoto: photo.isTopPhoto,
					},
				};
				const imageUrl = parseV0TemplateUrl({
					readUrl: photo.readUrl,
					direct: "original",
				});
				const videoUrl = parseV0TemplateUrl({
					readUrl: photo.readUrl,
					direct: "original-video",
				});
				switch (variant) {
					case PhotoVariant.LivePhoto:
						if (!imageUrl || !videoUrl) return null;
						return {
							type: "LivePhoto",
							url: imageUrl,
							videoUrl: videoUrl,
							successAmplitude,
						} satisfies SharePayload;
					case PhotoVariant.Video:
						if (!videoUrl) return null;
						return {
							type: "Video",
							videoUrl: videoUrl,
							successAmplitude,
						} satisfies SharePayload;
					case PhotoVariant.Image:
					default:
						if (!imageUrl) return null;
						return {
							type: "Image",
							url: imageUrl,
							successAmplitude,
						} satisfies SharePayload;
				}
			})
			.filter(Boolean) as SharePayload[];
	}, [selectedPhotos]);
	const renderItems = useCallback((options: ValidShareOption[]) => {
		const filteredOptions = options.filter(
			(option) => option.key === "ShareDownload" || option.key === "ShareShare",
		);

		return <FloatingSelectActionsContent options={filteredOptions} />;
	}, []);

	return <ShareWrapper data={shareData} renderItems={renderItems} onComplete={resetSelectMode} />;
};
export default memo(FloatingSelectActions);
